/**
 * The Dynamics board — Dynamics 365 Finance & Operations front-office health.
 *
 * Internally the source report is called "Sisu Performance Shield"; the board is
 * named after what it measures instead, because a codename tells a reader
 * nothing about whether their ERP is slow.
 *
 * Everything here is read live from two Azure Log Analytics workspaces by the
 * NOVA backend: F&O Application Insights telemetry (synthetic probes, form
 * loads, requests, dependencies) and a separate integration workspace for
 * Service Bus queue metrics. Nothing is seeded, sampled, or simulated.
 *
 * ## The state that matters most
 *
 * **This board's normal appearance today is not "ok".** As of 2026-08-20 neither
 * workspace can be read, and they fail differently: the F&O workspace resolves
 * and then answers 403 because a `Log Analytics Reader` assignment is pending,
 * while the integration workspace answers 404 because nothing exists at the
 * resource ID the Power BI model named. So `access_state` is the primary state
 * here, not an afterthought — six `access_state` values, of which the five
 * non-`ok` ones each carry their own panel, their own remedy, and their own
 * ticket. See `./access-panel.tsx`.
 *
 * When a panel's state is not `ok`, its charts do not render at all. Not an
 * empty axis, not a zero, not a flat line along the bottom — a 404 rendered as
 * a zero backlog would read as a perfectly drained integration layer, which is
 * the single most misleading thing this board could say.
 *
 * ## Reading order — what to do, before what is
 *
 * The board used to open with synthetic-availability KPIs. That is the weakest
 * evidence on the page: `NAX365` is a URL ping that follows redirects, matches
 * no content and never signs in, and over the measured week it ran 26,230 times
 * and failed zero times — including in the hours users waited eight seconds for
 * a form. Leading with it answered a question nobody arrived with, in the
 * flattering direction.
 *
 * A reader arriving here wants three answers in one order: **is anyone
 * waiting, is anything broken, did anyone complain.** So the page runs:
 *
 * 1. the lookback row, above everything it scopes;
 * 2. the L0 strip — slow interactions, form P95, batch throttling, open
 *    reports — four figures a person would act on and nothing else;
 * 3. **slow interactions**, the section with the most evidence behind it;
 * 4. **form performance**, the estate-wide experience percentiles;
 * 5. **batch throttling**, a platform fault no probe outside the tenant can
 *    see;
 * 6. **request health** and **Service Bus**, the integration path;
 * 7. **the control**: what the front door answered, from outside — every
 *    availability number kept, framed as what it can and cannot settle, with
 *    its caveat rendered beside the figures.
 *
 * The one thing that still comes first whatever the order is an unreadable
 * workspace: `access_state` is stated at the top, because "the board could not
 * ask" outranks everything the board would have said.
 *
 * ## Why there is no board-wide filter row
 *
 * The design rules ask for one filter row above everything it scopes (§4.3),
 * and the original four routes have none: none of them takes a window or a
 * dimension parameter, and their windows genuinely differ (overview 24 h,
 * user experience 7 days, requests 24 h, integration 6 h). A `DateRangePicker`
 * that re-rendered the same fixed windows would be a control that does nothing,
 * so each panel states its own window in its caption instead. The two places a
 * control does scope something real get one: result codes on the L2 requests
 * page (`FilterRow`), and the console's lookback (`LookbackRow`, `?lookback=`).
 * The lookback row sits above the KPI strip rather than beside the slow
 * section, because the strip's slow-interaction tile is scoped by it too and a
 * control below what it scopes is a control a reader finds second.
 *
 * ## What this board owns, and what `/experience` owns
 *
 * This board is the F&O **server** side: availability, forms, requests, Service
 * Bus queues, and the slow interactions with their classification and the Cato
 * network half. `/experience` is **browser** experience across the five apps the
 * managed extension reports on — TTFB, interaction latency, errors, idle probes,
 * the coverage ratio, and the ingest health of the collectors behind all of it.
 *
 * F&O legitimately appears on both. What must not appear on both is the same
 * *figure*, because then two boards can disagree and nothing says which is
 * stale. So browser coverage and collector freshness are `/experience`'s, and
 * the slow section carries a `BoardCrossLink` to them rather than a second copy
 * — which is also why the collector strip that used to sit at the top of this
 * board is not here any more: a business board leading with the health of its
 * own plumbing buries the question it exists to answer.
 *
 * ## Altitudes
 *
 * - **L0** — the KPI strip: four numbers, each of which changes what somebody
 *   does. Slow interactions (with the Cato coverage that qualifies its network
 *   half), form load P95 with the form-load base it was computed from, batch
 *   throttling events, and the open user-report count — the last one linking
 *   across to the queue on `/dynamics/reports` rather than down the page. The
 *   figures that used to fill the strip and answer nothing — probe availability,
 *   failed probe runs, form-load volume, the environment count — are all still
 *   on the board, in the sections that can frame them.
 * - **L1** — the slow-interaction section (classification by hour and share,
 *   worst forms and sites); form P95 trend, duration histogram and hour-of-day
 *   load; batch throttling; result-code donut and failing dependencies; queue
 *   backlog and its trend; and last, the control: availability trend and
 *   per-environment probe failures. Plus "worst 5" grids, never full lists.
 * - **L2** — `/dynamics/forms`, `/dynamics/requests`, `/dynamics/queues`,
 *   `/dynamics/investigate`, `/dynamics/reports`, and the per-environment,
 *   per-queue and per-record drawers. All URL-addressable, all reached from the
 *   sidebar's Dynamics group — this feature has no sub-nav of its own.
 *
 * ## The per-user layer, and where it lives
 *
 * The aggregate routes expose no user identifiers and no SQL statement text, so
 * the report's `TopUsersPain` and `HelpdeskTriage` pages — which ranked named
 * employees by a pain score — have no equivalent on this board, and never will:
 * cohorts below a k≥5 floor fold into an unlabelled "Other" row and every panel
 * that ranks cohorts states the fold.
 *
 * The per-user view exists, but one click deeper and on different terms:
 * `/dynamics/investigate` and `/dynamics/reports` (the F&O Performance Console,
 * concept §7 L2; D1 go, 2026-08-21). Both are open to the sign-ins named in
 * `NOVA_FO_INVESTIGATORS` only, and the sidebar disables their rows for anyone
 * else rather than offering a door onto a 403. It is **on demand** (computed by `FoSlowInteractions()` in the
 * workspace, nothing identity-bearing is stored in NOVA or persisted in the
 * browser), **reason-gated** (every per-user call carries a ticket/report id or
 * free text the investigator states once per session — D7, no separate role
 * or group) and **audit-logged** (who looked at whom, why; the list is on the
 * page itself). It shows slowness suffered, never work done: no rankings of
 * people, no page views per user, no time on form. Shared shop-floor and system
 * accounts are never attributable to a person and never appear there.
 */

import { Icon, RefreshCw } from "@norican/icons";
import { Alert, Button, Freshness, PageHeader, Skeleton } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";

import { NAV_SECTIONS } from "@/components/nav-sections";
import {
  useFoConsoleSlowSummary,
  useFoIntegration,
  useFoOverview,
  useFoRequests,
  useFoUserExperience,
} from "@/lib/api";
import { logger } from "@/lib/logger";

import { FoAccessPanel, FoErrorsAlert, type FoPanelState, isPanelOk } from "./access-panel";
import { AvailabilityControlSection, ThrottlingPanel } from "./availability-section";
import { ExperienceSection } from "./experience-section";
import { IntegrationSection } from "./integration-section";
import { DynamicsKpiStrip } from "./kpi-strip";
import { LookbackRow, useLookbackParam } from "./lookback-row";
import { OpenReportsTile } from "./reports-section";
import { RequestsSection } from "./requests-section";
import { SlowSection } from "./slow-section";

const SECTION = NAV_SECTIONS.find((section) => section.label === "Dynamics");

export { DynamicsFormsPage } from "./forms-page";
export { DynamicsInvestigatePage } from "./investigate-page";
export {
  DYNAMICS_FORMS_PATH,
  DYNAMICS_INVESTIGATE_PATH,
  DYNAMICS_PATH,
  DYNAMICS_PINPOINT_PATH,
  DYNAMICS_QUEUES_PATH,
  DYNAMICS_REPORTS_PATH,
  DYNAMICS_REQUESTS_PATH,
} from "./params";
export { DynamicsPinpointPage } from "./pinpoint-page";
export { DynamicsQueuesPage } from "./queues-page";
export { DynamicsReportsPage } from "./reports-page";
export { DynamicsRequestsPage } from "./requests-page";

export function DynamicsPage() {
  const queryClient = useQueryClient();
  const overview = useFoOverview();
  const lookback = useLookbackParam();
  // The console reads the same F&O workspace plus the two custom tables. It
  // reports its own access state, so it is not gated on the overview's.
  const slowSummary = useFoConsoleSlowSummary(lookback);
  const slowData = slowSummary.data;

  // With no Azure credential at all, the other three routes would answer with
  // the same "nothing was asked" payload. Skip them rather than spending three
  // round-trips to be told the same thing the board is already explaining.
  const credentialMissing = overview.data?.access_state === "no_credential";
  const experience = useFoUserExperience(!credentialMissing);
  const requests = useFoRequests(!credentialMissing);
  // Integration reads a *different* workspace in a different subscription, so it
  // is never gated on the F&O workspace's state — only on the credential.
  const integration = useFoIntegration();

  const data = overview.data;
  const refreshing =
    overview.isFetching ||
    experience.isFetching ||
    requests.isFetching ||
    integration.isFetching ||
    slowSummary.isFetching;

  // The backend rebuilds its snapshot on its own TTL; flag the data as stale
  // once it is meaningfully older than one rebuild window, not the instant it
  // ages past it.
  const staleAfterMs = data ? data.cache_ttl_sec * 2000 : undefined;

  /**
   * Whether a panel's failure has already been explained in full above.
   *
   * Three of the four panels read the same workspace, so an unreadable
   * workspace produces three identical remediation lists — which reads as three
   * separate faults. The first one explains it; the rest say "same cause".
   * Same reasoning as the backend folding identical `errors[]` causes into one
   * line.
   */
  const alreadyExplained = (panel: FoPanelState): boolean =>
    data != null &&
    !isPanelOk(data) &&
    panel.access_state === data.access_state &&
    // `no_credential` is the one workspace-independent state: nothing was asked
    // of Azure at all, so it is a single fault however many workspaces would
    // have been read. Every other state belongs to one specific workspace, and
    // the integration panel reads a different one — so a 404 there is never
    // "the same cause" as a 403 on the F&O workspace.
    (panel.access_state === "no_credential" || panel.workspace === data.workspace);

  const refresh = () => {
    logger.debug("[fo] manual refresh requested");
    void queryClient.invalidateQueries({ queryKey: ["fo"] });
  };

  const experienceData = experience.data;
  const requestsData = requests.data;
  const integrationData = integration.data;

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Dynamics"
        subtitle={
          SECTION?.description ??
          "Dynamics 365 Finance & Operations front-office health, read live from Log Analytics."
        }
        actions={
          <div className="flex items-center gap-4">
            {data?.collected_at && (
              <Freshness
                label="Log Analytics read"
                since={data.collected_at}
                staleAfter={staleAfterMs}
                pulse={refreshing}
              />
            )}
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={refreshing}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>
        }
      />

      {overview.error && (
        <Alert tone="danger" title="Couldn't load the Dynamics board">
          {overview.error instanceof Error
            ? overview.error.message
            : "The NOVA API did not respond. This is a NOVA problem, not an Azure one."}
        </Alert>
      )}

      {slowSummary.error && (
        <Alert tone="danger" title="Couldn't load the slow-interaction console">
          The NOVA API did not answer `/fo/console/slow-summary`. This is a NOVA problem, not an
          Azure one; the front-office panels below are unaffected.
        </Alert>
      )}

      {overview.isPending && !data ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="7rem" />
            ))}
          </div>
          <Skeleton height="20rem" />
          <Skeleton height="20rem" />
        </div>
      ) : (
        data && (
          <>
            {/* ── Whether the board could ask at all ────────────────────
                Stated first whatever the reading order, because "the
                workspace could not be read" outranks every number below,
                and `alreadyExplained` folds the repeats against it. The
                panel names what this payload feeds: the two overview KPI
                tiles, the throttling panel and the control section. */}
            {!isPanelOk(data) && (
              <FoAccessPanel
                panel="Front-office KPIs, batch throttling and the synthetic control"
                state={data}
              />
            )}
            {isPanelOk(data) && data.errors.length > 0 && <FoErrorsAlert errors={data.errors} />}

            {/* ── A. What to do (L0) ────────────────────────────────────
                One filter row above everything it scopes (§4.3): the
                lookback scopes the strip's slow-interaction tile as well as
                the section below, so it sits above both.

                The strip reads three payloads that fail independently, and
                each contributes tiles only while its own `access_state` is
                ok — the console reads the same workspace plus two custom
                tables and reports its own state, so it can be ok while the
                overview is not, and the report queue answers 403 for a
                reader who is not an investigator. A payload that is not ok
                contributes no tile at all; its access panel is elsewhere on
                this page. */}
            <LookbackRow scopes="the slow-interaction tiles and section" />
            <DynamicsKpiStrip
              summary={isPanelOk(data) ? data.summary : null}
              window={data.window}
              slow={slowData && isPanelOk(slowData) ? slowData : null}
              reportsTile={<OpenReportsTile />}
            />

            {/* ── B. Is anyone waiting — the F&O Performance Console (L1) ─ */}
            <div className="flex flex-col gap-4">
              {slowSummary.isPending && !slowData && !slowSummary.error && (
                <Skeleton height="20rem" />
              )}
              {slowData && !isPanelOk(slowData) && (
                <FoAccessPanel
                  panel="Slow interactions (F&O Performance Console)"
                  state={slowData}
                  repeated={alreadyExplained(slowData)}
                />
              )}
              {slowData && isPanelOk(slowData) && (
                <>
                  {slowData.errors.length > 0 && <FoErrorsAlert errors={slowData.errors} />}
                  <SlowSection summary={slowData} lookback={lookback} />
                </>
              )}
            </div>

            {/* ── C. Form performance (L1) ──────────────────────────────── */}
            {credentialMissing && <FoAccessPanel panel="Form performance" state={data} repeated />}
            {!credentialMissing && experienceData && isPanelOk(experienceData) && (
              <>
                {experienceData.errors.length > 0 && (
                  <FoErrorsAlert errors={experienceData.errors} />
                )}
                <ExperienceSection experience={experienceData} />
              </>
            )}
            {!credentialMissing && experienceData && !isPanelOk(experienceData) && (
              <FoAccessPanel
                panel="Form performance"
                state={experienceData}
                repeated={alreadyExplained(experienceData)}
              />
            )}

            {/* ── D. Is anything broken on the platform (L1) ─────────────
                Batch throttling is F&O's own signal, not the probe's — jobs
                queued behind the throttle, invisible to anything outside the
                tenant. It used to sit inside the availability section, which
                filed a real platform fault under a synthetic control. It is
                its own panel now, above the control and below the pain. */}
            {isPanelOk(data) && <ThrottlingPanel overview={data} />}

            {/* ── E. Request health (L1) ────────────────────────────────── */}
            {credentialMissing && (
              <FoAccessPanel
                panel="API gateway (Management in front of F&O)"
                state={data}
                repeated
              />
            )}
            {!credentialMissing && requestsData && isPanelOk(requestsData) && (
              <>
                {requestsData.errors.length > 0 && <FoErrorsAlert errors={requestsData.errors} />}
                <RequestsSection requests={requestsData} />
              </>
            )}
            {!credentialMissing && requestsData && !isPanelOk(requestsData) && (
              <FoAccessPanel
                panel="API gateway (Management in front of F&O)"
                state={requestsData}
                repeated={alreadyExplained(requestsData)}
              />
            )}

            {/* ── F. Service Bus integration (L1) — the second workspace ── */}
            {integrationData && isPanelOk(integrationData) && (
              <>
                {integrationData.errors.length > 0 && (
                  <FoErrorsAlert errors={integrationData.errors} />
                )}
                <IntegrationSection integration={integrationData} />
              </>
            )}
            {integrationData && !isPanelOk(integrationData) && (
              <FoAccessPanel
                panel="Service Bus queue health"
                state={integrationData}
                resourceId={integrationData.workspace_resource_id}
                namespace={integrationData.namespace}
                repeated={alreadyExplained(integrationData)}
              />
            )}

            {/* ── G. The control (L1) ───────────────────────────────────
                Last, and framed as what it is: a URL ping from outside that
                rules out the network path and the front door and settles
                nothing else. Every availability number the strip used to
                lead with is here, with its caveat rendered beside it. It
                also owns the `?env=` drawer that the throttling panel above
                writes into — one param, one overlay. */}
            {isPanelOk(data) && <AvailabilityControlSection overview={data} />}
          </>
        )
      )}
    </main>
  );
}
